import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@itaas/core/auth/useAuth";
import { PageHeader } from "@/components/PageHeader";
import { TablePagination } from "@/components/TablePagination";
import { useClientPagination } from "@/hooks/useClientPagination";
import {
  consultationDetailPath,
  createPharmacistDirectPrescription,
  getConsultationById,
  isPharmacistDirectPrescription,
  listPrescriptions,
  markPrescriptionSent,
  syncPrescriptionsFromApi,
} from "@/core/clinical/clinical-store";
import {
  PRESCRIPTION_SOURCE_LABELS,
  PRESCRIPTION_STATUS_LABELS,
} from "@/core/clinical/types";
import { getPatientById, listPatients, syncPatientsFromApi } from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";
import { notifyPrescriptionSentToPharmacy } from "@/core/notifications/pharmacy-notify";
import {
  canCreatePharmacistDirectPrescription,
} from "@/core/permissions";
import { routes } from "@/router/all_routes";

export function PrescriptionsPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const canDirectRx = canCreatePharmacistDirectPrescription(auth);
  const [refreshKey, setRefreshKey] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [directPatientId, setDirectPatientId] = useState("");
  const [directError, setDirectError] = useState<string | null>(null);
  const [sentMsg, setSentMsg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSyncing(true);
    void (async () => {
      await syncPatientsFromApi();
      await syncPrescriptionsFromApi();
      if (!cancelled) {
        setSyncing(false);
        setRefreshKey((k) => k + 1);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const prescriptions = useMemo(() => {
    void refreshKey;
    return listPrescriptions();
  }, [refreshKey]);

  const patients = useMemo(() => {
    void refreshKey;
    return listPatients();
  }, [refreshKey]);

  const pagination = useClientPagination(prescriptions, [], 25);
  const { pagedItems } = pagination;

  const refresh = () => setRefreshKey((k) => k + 1);

  const handleSendToPharmacy = (prescriptionId: string) => {
    const rx = prescriptions.find((item) => item.id === prescriptionId);
    if (!rx || rx.lines.length === 0) return;
    const patient = getPatientById(rx.patientId);
    markPrescriptionSent(prescriptionId);
    void notifyPrescriptionSentToPharmacy({
      prescriptionId,
      patientName: patient ? patientFullName(patient) : "Patient",
    });
    setSentMsg("Ordonnance envoyée à la pharmacie — en file d'attente officine.");
    refresh();
  };

  const handleNewDirectPrescription = (e: FormEvent) => {
    e.preventDefault();
    if (!canDirectRx) return;
    if (!directPatientId) {
      setDirectError("Sélectionnez un patient.");
      return;
    }
    const { consultation } = createPharmacistDirectPrescription(directPatientId);
    setDirectPatientId("");
    setDirectError(null);
    navigate(consultationDetailPath(consultation.id));
  };

  return (
    <div>
      <PageHeader
        title="Prescriptions"
        description={
          syncing
            ? "Synchronisation durable en cours…"
            : "Ordonnances médicales — envoi vers la pharmacie (sans facturation)."
        }
        actions={
          <Link to={routes.consultations} className="btn btn-outline-secondary btn-sm">
            Consultations
          </Link>
        }
      />

      {sentMsg && <div className="alert alert-success py-2">{sentMsg}</div>}

      {canDirectRx && (
        <div className="ph-invoice-sheet mb-4">
          <h3 className="h6 fw-semibold mb-2">Nouvelle ordonnance (officine)</h3>
          <p className="small text-muted mb-3">
            Saisie directe par le pharmacien — option activée par l&apos;administrateur.
          </p>
          <form className="row g-2 align-items-end ph-lab-register-form" onSubmit={handleNewDirectPrescription}>
            <div className="col-12 col-md-5">
              <label className="form-label small fw-semibold mb-1" htmlFor="direct-rx-patient">
                Patient
              </label>
              <select
                id="direct-rx-patient"
                className="form-select form-select-sm"
                value={directPatientId}
                onChange={(e) => setDirectPatientId(e.target.value)}
              >
                <option value="">— Choisir —</option>
                {patients.map((p) => (
                  <option key={p.id} value={p.id}>
                    {patientFullName(p)} ({p.fileNumber})
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-3">
              <button type="submit" className="btn btn-primary btn-sm w-100">
                Nouvelle ordonnance
              </button>
            </div>
          </form>
          {directError && <p className="small text-danger mb-0 mt-2">{directError}</p>}
        </div>
      )}

      <div className="ph-invoice-sheet">
        {prescriptions.length === 0 ? (
          <p className="text-muted mb-0">
            Aucune ordonnance — créez une consultation puis ajoutez des lignes médicament.
          </p>
        ) : (
          <div className="table-responsive ph-diag-table">
            <table className="table table-sm ph-dash-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Patient</th>
                  <th>Origine</th>
                  <th>Lignes</th>
                  <th>Statut</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedItems.map((rx) => {
                  const patient = getPatientById(rx.patientId);
                  const consultation = getConsultationById(rx.consultationId);
                  const source = rx.source ?? "clinical";
                  const isDirect = isPharmacistDirectPrescription(rx);
                  return (
                    <tr key={rx.id} className="ph-diag-row">
                      <td data-label="Date" className="small text-muted">
                        {new Date(rx.createdAt).toLocaleString("fr-FR")}
                      </td>
                      <td data-label="Patient" className="fw-medium">
                        {patient ? patientFullName(patient) : "—"}
                      </td>
                      <td data-label="Origine" className="small">
                        {PRESCRIPTION_SOURCE_LABELS[source]}
                      </td>
                      <td data-label="Lignes" className="small">
                        {rx.lines.length} article(s)
                      </td>
                      <td data-label="Statut">
                        <span className={`hosp-rx-badge hosp-rx-badge--${rx.status}`}>
                          {PRESCRIPTION_STATUS_LABELS[rx.status]}
                        </span>
                      </td>
                      <td data-label="Actions" className="text-end">
                        {consultation && (
                          <Link
                            to={consultationDetailPath(consultation.id)}
                            className="btn btn-link btn-sm p-0 me-2"
                          >
                            {isDirect ? "Saisir" : "Consultation"}
                          </Link>
                        )}
                        {rx.lines.length > 0 &&
                          rx.status === "draft" &&
                          !isDirect && (
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0"
                              onClick={() => handleSendToPharmacy(rx.id)}
                            >
                              Envoyer pharmacie
                            </button>
                          )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <TablePagination
              page={pagination.page}
              pageSize={pagination.pageSize}
              totalItems={pagination.totalItems}
              onPageChange={pagination.setPage}
              onPageSizeChange={pagination.setPageSize}
            />
          </div>
        )}
      </div>
    </div>
  );
}
